import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Alert, Button, Modal, Result, Space, Spin, Tag, message } from 'antd';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ActionPopoverButton, {
  ActionPopoverElement,
} from '../../components/ActionPopoverButton';
import ContentCard from '../../components/ContentCard';
import { Permission } from '../../user/types/Permission';
import { useUser } from '../../user/userContext';
import { userHasPermission } from '../../user/userUtils';
import OpenClassAPI from '../OpenClassAPI';
import OpenClassEditModal from '../components/OpenClassEditModal';
import DetailField from '../components/DetailField';
import OpenClassDateAndLocation from '../components/OpenClassDateAndLocation';
import SessionCapacityList from '../components/SessionCapacityList';
import OpenClassSessionDetailsCard from '../components/OpenClassSessionDetailsCard';
import OpenClassAttendeesModal from '../components/OpenClassAttendeesModal';
import OpenClassSessionAttendeesModal from '../components/OpenClassSessionAttendeesModal';
import AddOpenClassAttendeeModal from '../components/AddOpenClassAttendeeModal';
import OpenClassSendCertificatesModal from '../components/OpenClassSendCertificatesModal';
import OpenClassSessionFormModal from '../components/OpenClassSessionFormModal';
import OpenClassStatusBadge from '../components/OpenClassStatusBadge';
import { useCourses } from '../hooks/useCourses';
import {
  OpenClass,
  OpenClassRegistration,
  OpenClassSession,
  OpenClassStatus,
  UpdateOpenClassSessionParams,
} from '../types/OpenClass';
import { sessionOutcomesBySession } from '../utils/sessionOutcome';
import { sortSessionsByStart } from '../utils/openClass';
import { certificateTypeLabel } from '../utils/certificateTypeLabel';

const openClassesPath = '/oppna-kurstillfallen';

const OpenClassDetails: React.FC = () => {
  const { openClassId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [currentUser] = useUser();
  const [openClass, setOpenClass] = useState<OpenClass | null>(null);
  const [registrations, setRegistrations] = useState<OpenClassRegistration[]>(
    [],
  );
  const [attendeesSession, setAttendeesSession] =
    useState<OpenClassSession | null>(null);
  const [attendeesModalOpen, setAttendeesModalOpen] = useState(false);
  const [addAttendeeOpen, setAddAttendeeOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const { canReadCourses, courses } = useCourses();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editingClass, setEditingClass] = useState(false);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<OpenClassSession | null>(
    null,
  );

  const canUpdate = userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_UPDATE,
  );
  const canCreate = userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_CREATE,
  );
  const canDelete = userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_DELETE,
  );
  const scopedToOwnSessions = !userHasPermission(
    currentUser,
    Permission.COURSE_INSTANCE_READ_GLOBAL,
  );

  const id = Number(openClassId);

  const loadOpenClass = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    try {
      const { data } = await OpenClassAPI.getOpenClass(id);
      setOpenClass(data);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        setNotFound(true);
      } else {
        message.error(t('views.OpenClasses.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadOpenClass();
  }, [loadOpenClass]);

  // One request feeds both the per-session counts and the attendance list.
  const loadRegistrations = useCallback(async () => {
    if (!Number.isFinite(id)) return;
    try {
      const { data } = await OpenClassAPI.getOpenClassRegistrations(id);
      setRegistrations(data);
    } catch {
      setRegistrations([]);
    }
  }, [id]);

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  const sessionOutcomes = useMemo(
    () => sessionOutcomesBySession(registrations),
    [registrations],
  );

  const openSessionEditor = useCallback((session?: OpenClassSession) => {
    setEditingSession(session ?? null);
    setSessionModalOpen(true);
  }, []);

  const deleteSession = useCallback(
    (session: OpenClassSession) => {
      Modal.confirm({
        content: t('views.OpenClasses.deleteSessionConfirm'),
        okButtonProps: { danger: true },
        title: t('common.delete'),
        onOk: async () => {
          try {
            await OpenClassAPI.deleteSession(id, session.id);
            await loadOpenClass();
          } catch (error) {
            if (isAxiosError(error) && error.response?.status === 409) {
              // With sessions left over, recorded attendance is the only conflict.
              message.error(
                (openClass?.sessions.length ?? 0) > 1
                  ? t('views.OpenClasses.sessionHasAttendanceDeleteError')
                  : t('views.OpenClasses.lastSessionDeleteError'),
              );
            } else {
              message.error(t('views.OpenClasses.deleteFailed'));
            }
          }
        },
      });
    },
    [id, loadOpenClass, openClass, t],
  );

  const changeSessionLifecycle = useCallback(
    async (
      session: OpenClassSession,
      changes: Pick<UpdateOpenClassSessionParams, 'confirmed' | 'canceled'>,
    ) => {
      const params: UpdateOpenClassSessionParams = {
        startAt: session.startAt,
        endAt: session.endAt,
        capacity: session.capacity,
        instructorIds: session.instructors
          .map(({ id }) => id)
          .filter(
            (instructorId): instructorId is number => instructorId != null,
          ),
        location: session.location ?? null,
        comment: session.comment ?? null,
        ...changes,
      };
      try {
        await OpenClassAPI.updateSession(id, session.id, params);
        await loadOpenClass();
      } catch (error) {
        if (isAxiosError(error) && !error.response?.data.message) {
          message.error(t('views.OpenClasses.saveFailed'));
        }
      }
    },
    [id, loadOpenClass, t],
  );

  const cancelSession = useCallback(
    (session: OpenClassSession) => {
      Modal.confirm({
        content: t('views.OpenClasses.cancelSessionConfirm'),
        okButtonProps: { danger: true },
        okText: t('common.confirm'),
        title: t('views.OpenClasses.cancelSessionTitle'),
        onOk: () => changeSessionLifecycle(session, { canceled: true }),
      });
    },
    [changeSessionLifecycle, t],
  );

  const sessionActionElements = (
    session: OpenClassSession,
  ): ActionPopoverElement[] => {
    const isCanceled = session.status === OpenClassStatus.Canceled;
    const isConfirmed = session.status === OpenClassStatus.Confirmed;
    const hasInstructor = session.instructors.length > 0;

    return [
      {
        rowElement: (
          <div>
            {isConfirmed ? (
              <>
                <UndoOutlined className="pr-2" />
                {t('views.OpenClasses.unconfirmSession')}
              </>
            ) : (
              <>
                <CheckOutlined className="pr-2" />
                {t('views.OpenClasses.confirmSession')}
              </>
            )}
          </div>
        ),
        action: () =>
          changeSessionLifecycle(session, { confirmed: !isConfirmed }),
        hidden: !canUpdate || isCanceled,
        disabled: !isConfirmed && !hasInstructor,
        tooltip:
          !isConfirmed && !hasInstructor
            ? t('views.OpenClasses.confirmNeedsInstructor')
            : undefined,
      },
      {
        rowElement: (
          <div>
            <EditOutlined className="pr-2" />
            {t('common.edit')}
          </div>
        ),
        action: () => openSessionEditor(session),
        hidden: !canUpdate,
      },
      {
        rowElement: (
          <div>
            {isCanceled ? (
              <>
                <ReloadOutlined className="pr-2" />
                {t('views.OpenClasses.reactivateSession')}
              </>
            ) : (
              <>
                <CloseCircleOutlined className="pr-2" />
                {t('views.OpenClasses.cancelSessionTitle')}
              </>
            )}
          </div>
        ),
        action: () =>
          isCanceled
            ? changeSessionLifecycle(session, { canceled: false })
            : cancelSession(session),
        hidden: !canUpdate,
      },
      {
        rowElement: (
          <div>
            <DeleteOutlined className="pr-2" />
            {t('common.delete')}
          </div>
        ),
        action: () => deleteSession(session),
        hidden: !canDelete,
      },
    ];
  };

  const deleteOpenClass = useCallback(() => {
    Modal.confirm({
      content: t('views.OpenClasses.deleteOpenClassConfirm'),
      okButtonProps: { danger: true },
      title: t('common.delete'),
      onOk: async () => {
        try {
          await OpenClassAPI.deleteOpenClass(id);
          navigate(openClassesPath);
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 409) {
            message.error(t('views.OpenClasses.classDeleteRegistrationsError'));
          } else {
            message.error(t('views.OpenClasses.deleteFailed'));
          }
        }
      },
    });
  }, [id, navigate, t]);

  const classActionElements: ActionPopoverElement[] = [
    {
      rowElement: (
        <div>
          <EditOutlined className="pr-2" />
          {t('common.edit')}
        </div>
      ),
      action: () => setEditingClass(true),
      hidden: !canUpdate || !canReadCourses,
    },
    {
      rowElement: (
        <div className="text-warningRed">
          <DeleteOutlined className="pr-2" />
          {t('common.delete')}
        </div>
      ),
      action: deleteOpenClass,
      hidden: !canDelete,
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center pt-20">
        <Spin size="large" />
      </div>
    );
  }

  if (notFound || !openClass) {
    return (
      <Result
        status="404"
        subTitle={t('views.OpenClasses.notFound')}
        extra={
          <Button onClick={() => navigate(openClassesPath)}>
            {t('common.goBack')}
          </Button>
        }
      />
    );
  }

  const sortedSessions = sortSessionsByStart(openClass.sessions);

  return (
    <>
      <div className="sm:flex sm:flex-row items-center pb-2">
        <button
          type="button"
          className="hover:underline cursor-pointer text-blueGrayDark bg-transparent border-0 p-0 font-[inherit]"
          onClick={() =>
            (location.state as { prevPage?: string } | null)?.prevPage
              ? navigate(-1)
              : navigate(openClassesPath)
          }>
          <ArrowLeftOutlined style={{ fontSize: 11 }} />
          <span className="pl-1 pt-0.5 text-xs">{t('common.goBack')}</span>
        </button>
      </div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-4">
        <h2 className="m-0">{t('views.OpenClasses.editOpenClass')}</h2>
        <Space>
          <Button onClick={() => setAttendeesModalOpen(true)}>
            {t('views.OpenClasses.manageAttendees')}
          </Button>
          {canUpdate && (
            // Always reachable, the sheet is where an admin finds out who is getting nothing
            // and why, which is worth opening even when there is nothing left to send.
            <Button
              icon={<MailOutlined />}
              onClick={() => setEmailModalOpen(true)}>
              {t('views.OpenClasses.sendCertificates')}
            </Button>
          )}
          {canUpdate && (
            <Button
              icon={<PlusOutlined />}
              type="primary"
              onClick={() => setAddAttendeeOpen(true)}>
              {t('views.OpenClasses.addParticipant')}
            </Button>
          )}
          {(canUpdate || canDelete) && (
            <ActionPopoverButton
              absolutePlacement={false}
              actionPopoverElements={classActionElements}
              placement="bottomRight"
            />
          )}
        </Space>
      </div>

      {scopedToOwnSessions && (
        <Alert
          className="mb-4"
          message={t('views.OpenClasses.scopedToOwnSessionsInClass')}
          showIcon
          type="info"
        />
      )}

      <ContentCard>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6">
          <DetailField label={t('common.status')}>
            <OpenClassStatusBadge
              endAt={openClass.endAt}
              status={openClass.status}
            />
          </DetailField>
          <DetailField label={t('views.OpenClasses.sessions')}>
            {t('views.OpenClasses.sessionCount', {
              count: openClass.sessions.length,
            })}
          </DetailField>
          <DetailField label={t('views.OpenClasses.dateAndLocation')}>
            <OpenClassDateAndLocation openClass={openClass} />
          </DetailField>
          <DetailField label={t('views.OpenClasses.participants')}>
            <SessionCapacityList sessions={openClass.sessions} />
          </DetailField>
          <DetailField label={t('common.course')}>
            <span className="text-safeLifeMedium">{openClass.course.name}</span>
          </DetailField>
          <DetailField label={t('views.OpenClasses.certificateTypes')}>
            <Space size={[4, 4]} wrap>
              {openClass.certificateTemplates.map(({ id, type }) => (
                <Tag
                  key={id}
                  className="m-0 border-0 bg-blueGrayLight text-safeLifeMedium">
                  {certificateTypeLabel(type)}
                </Tag>
              ))}
            </Space>
          </DetailField>
        </div>

        <div className="mt-10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-courseInstanceIncomplete text-sm font-semibold text-white">
              {sortedSessions.length}
            </span>
            <h3 className="m-0">{t('views.OpenClasses.classSessions')}</h3>
          </div>
          {canCreate && (
            <Button icon={<PlusOutlined />} onClick={() => openSessionEditor()}>
              {t('views.OpenClasses.addSession')}
            </Button>
          )}
        </div>

        <div className="ml-4 border-0 border-l-2 border-solid border-courseInstanceIncomplete pl-6">
          {sortedSessions.map((session, index) => (
            <div
              key={session.id}
              className={`pt-6 ${
                index > 0
                  ? 'border-0 border-t border-solid border-gray-200'
                  : ''
              }`}>
              <OpenClassSessionDetailsCard
                actions={sessionActionElements(session)}
                course={openClass.course}
                outcome={sessionOutcomes.get(session.id)}
                session={session}
                showActions={canUpdate || canDelete}
                onShowAttendees={() => setAttendeesSession(session)}
              />
            </div>
          ))}
        </div>
      </ContentCard>

      {canUpdate && canReadCourses && (
        <OpenClassEditModal
          courses={courses}
          open={editingClass}
          openClass={openClass}
          onClose={() => setEditingClass(false)}
          onSaved={loadOpenClass}
        />
      )}
      {attendeesModalOpen && (
        <OpenClassAttendeesModal
          canUpdate={canUpdate}
          openClass={openClass}
          open
          registrations={registrations}
          onClose={() => setAttendeesModalOpen(false)}
          onUpdated={loadRegistrations}
        />
      )}
      {addAttendeeOpen && (
        <AddOpenClassAttendeeModal
          openClass={openClass}
          open
          registrations={registrations}
          onClose={() => setAddAttendeeOpen(false)}
          onSaved={async () => {
            await Promise.all([loadOpenClass(), loadRegistrations()]);
          }}
        />
      )}
      <OpenClassSendCertificatesModal
        open={emailModalOpen}
        openClass={openClass}
        registrations={registrations}
        onClose={() => setEmailModalOpen(false)}
        onCompleted={loadRegistrations}
      />
      {attendeesSession && (
        <OpenClassSessionAttendeesModal
          canUpdate={canUpdate}
          openClass={openClass}
          open
          registrations={registrations}
          session={attendeesSession}
          onClose={() => setAttendeesSession(null)}
          onUpdated={loadRegistrations}
        />
      )}
      <OpenClassSessionFormModal
        courseId={openClass.course.id}
        existingSessions={openClass.sessions}
        open={sessionModalOpen}
        openClassId={openClass.id}
        session={editingSession}
        onClose={() => setSessionModalOpen(false)}
        onSaved={loadOpenClass}
      />
    </>
  );
};

export default OpenClassDetails;
